import LoadSpinner from 'legacyfrontend/components/spinner';
import ErrorSnackbar from 'legacyfrontend/components/ErrorSnackbar';
import FlightCard from 'legacyfrontend/components/flights/FlightCard';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import { useFlightsFilters } from 'legacyfrontend/contexts/FlightsFiltersContext';
import { useCallback, useEffect, useMemo, useState, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import PackagesApi from '../../../api/paquetes';
import Flex from '../../../components/Flex';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import { useRefreshModal } from 'legacyfrontend/hooks/useRefreshModal';
import RefreshModal from 'legacyfrontend/components/home/components/RefreshModal';
import { useSelector } from 'react-redux';
import { CACHE_DEFAULTS, SESSION_STORAGE_KEYS } from 'legacyfrontend/constants';
import { useColors } from 'legacyfrontend/hooks/useColors';

const FlightsListView = () => {
  const { t } = useTranslation('layout');
  const { onSearchCount, registerOnSearch } = useGlobal();
  const { setData, filteredData, isLoading, setIsLoading, error, setError, filters, handleFilters } =
    useFlightsFilters();
  const { search } = useLocation();
  const { flightInfo, setFlightInformation, handleCacheExpiration, setOneWayFlight } = useFlightContext();
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const delegation = useSelector((state) => state.applicationState.delegation);
  const { showRefreshModal, setShowRefreshModal, handleRefreshConfirm } = useRefreshModal();
  const { background, text } = useColors();

  const hasCalledApiRef = useRef(false);
  const prevCurrencyRef = useRef(filters.currency);
  const prevCabinClassRef = useRef(filters.cabinClass);
  const isInitialCurrencySyncRef = useRef(false);
  const currencyChangeFromUrlRef = useRef(false);
  
  const getLastEssentialParams = useCallback(() => {
    try {
      return window.sessionStorage.getItem('lastEssentialSearchParams') || '';
    } catch (error) {
      return '';
    }
  }, []);
  
  const setLastEssentialParams = useCallback((params) => {
    try {
      if (params) {
        window.sessionStorage.setItem('lastEssentialSearchParams', params);
      } else {
        window.sessionStorage.removeItem('lastEssentialSearchParams');
      }
    } catch (error) {
      console.warn('Error saving lastEssentialSearchParams to sessionStorage:', error);
    }
  }, []);

  const searchParamsToObject = useMemo(() => {
    if (!search) return {};

    const params = new URLSearchParams(search);
    const filters = {};

    params.forEach((value, key) => {
      if (key === 'passengers') {
        filters[key] = value.split(',').map(Number);
      } else {
        filters[key] = value;
      }
    });

    return filters;
  }, [search]);

  const searchWithoutRefresh = useMemo(() => {
    if (!search) return '';
    const params = new URLSearchParams(search);
    params.delete('_refresh');
    params.delete('cache');
    return params.toString();
  }, [search]);

  const hasValidSearchParams = useMemo(() => {
    if (!search) return false;

    const searchParams = new URLSearchParams(search);
    const hasOrigin = !!(searchParams.get('originId') && searchParams.get('originName'));
    const hasDestination = !!(searchParams.get('destinationId') && searchParams.get('destinationName'));
    const hasDepartureDate = !!searchParams.get('departureDate');

    return hasOrigin && hasDestination && hasDepartureDate;
  }, [search]);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleLlegoData = useCallback(async () => {
    if (!hasValidSearchParams) {
      setData([]);
      setIsLoading(false);
      setError(false);
      return;
    }

    setError(false);

    const {
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
      currency: urlCurrency,
      isOnlyOneWay: urlIsOnlyOneWay,
    } = searchParamsToObject;

    if (getCacheStatus() === true) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
    }

    const cacheValue = getCacheStatus();

    const currency = urlCurrency && (urlCurrency === 'USD' || urlCurrency === 'ARS')
      ? urlCurrency
      : filters.currency;

    const isOnlyOneWay = urlIsOnlyOneWay === 'true' || urlIsOnlyOneWay === true;
    const cleanedReturnDate = isOnlyOneWay ? null : returnDate;
    
    setFlightInformation((prevFlightInfo) => {
      if (isOnlyOneWay !== prevFlightInfo?.isOnlyOneWay) {
        setOneWayFlight(isOnlyOneWay);
      }

      const updatedInfo = {
        ...prevFlightInfo,
        destinationId,
        destinationName,
        originId,
        originName,
        departureDate,
        returnDate: cleanedReturnDate,
        passengers,
        isOnlyOneWay,
      };

      return updatedInfo;
    });

    const data = {
      originId,
      destinationID: destinationId,
      origin: originName,
      destination: destinationName,
      passengers,
      departureDate,
      returnDate: cleanedReturnDate,
      direct: false,
      baggage: false,
      cabin: filters.cabinClass,
      currency,
      cache: cacheValue,
      delegation,
    };

    try {
      setIsLoading(true);
      const dataResponse = await PackagesApi.fetchLlegoData(data);
      setData(dataResponse?.data || []);
      handleCacheExpiration(
        dataResponse?.cacheExpiresAt ||
        new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME)
      );
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
      setError(false);

      const urlCurrency = searchParamsToObject.currency;
      if (urlCurrency && (urlCurrency === 'USD' || urlCurrency === 'ARS')) {
        if (filters.currency !== urlCurrency) {
          handleFilters('currency', urlCurrency);
          prevCurrencyRef.current = urlCurrency;
        }
      }
    } catch (error) {
      let errorMessage = 'Error al cargar los vuelos';

      if (error?.response?.status === 500) {
        errorMessage = 'Error del servidor. Por favor, intenta nuevamente en unos momentos.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'No se encontraron vuelos con los criterios especificados.';
      } else if (error?.code === 'ERR_NETWORK') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
      setError(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    hasValidSearchParams,
    searchParamsToObject,
    filters.cabinClass,
    filters.currency,
    delegation,
    setFlightInformation,
    handleCacheExpiration,
    setOneWayFlight,
    setData,
    setIsLoading,
    setError,
    showError,
    handleFilters
  ]);

  const handleRefresh = useCallback(() => {
    handleLlegoData();
  }, [handleLlegoData]);

  const handleLlegoDataRef = useRef(handleLlegoData);
  
  useEffect(() => {
    handleLlegoDataRef.current = handleLlegoData;
  }, [handleLlegoData]);

  const searchParamsWithoutRefreshRef = useRef(null);

  const getEssentialSearchParams = useCallback((searchString) => {
    if (!searchString) return '';
    const params = new URLSearchParams(searchString);
    const essentialParams = new URLSearchParams();
    const essentialKeys = ['originId', 'destinationId', 'departureDate', 'isOnlyOneWay', 'passengers'];
    essentialKeys.forEach(key => {
      const value = params.get(key);
      if (value) essentialParams.set(key, value);
    });
    const isOnlyOneWay = params.get('isOnlyOneWay');
    const returnDate = params.get('returnDate');
    if (returnDate && isOnlyOneWay !== 'true') {
      essentialParams.set('returnDate', returnDate);
    }
    return essentialParams.toString();
  }, []);

  useEffect(() => {
    if (!searchWithoutRefresh) return;

    const paramsWithoutCurrency = new URLSearchParams(searchWithoutRefresh);
    paramsWithoutCurrency.delete('currency');
    const searchWithoutCurrency = paramsWithoutCurrency.toString();

    const prevParamsWithoutCurrency = searchParamsWithoutRefreshRef.current
      ? new URLSearchParams(searchParamsWithoutRefreshRef.current)
      : new URLSearchParams();
    prevParamsWithoutCurrency.delete('currency');
    const prevSearchWithoutCurrency = prevParamsWithoutCurrency.toString();

    const currentEssential = getEssentialSearchParams(searchWithoutRefresh);
    const prevEssential = getEssentialSearchParams(searchParamsWithoutRefreshRef.current || '');
    const lastEssential = getLastEssentialParams();

    const onlyCurrencyChanged = searchParamsWithoutRefreshRef.current !== null &&
      prevSearchWithoutCurrency === searchWithoutCurrency &&
      searchParamsWithoutRefreshRef.current !== searchWithoutRefresh;

    const essentialParamsUnchanged = prevEssential !== '' && currentEssential === prevEssential;
    
    const isReturningWithSameParams = prevEssential === '' && 
      currentEssential !== '' && 
      currentEssential === lastEssential &&
      searchParamsWithoutRefreshRef.current !== null &&
      hasCalledApiRef.current;

    const searchParamsChanged = !essentialParamsUnchanged && !isReturningWithSameParams;

    if (searchParamsChanged) {
      hasCalledApiRef.current = false;
      searchParamsWithoutRefreshRef.current = searchWithoutRefresh;
      setLastEssentialParams(currentEssential);
      currencyChangeFromUrlRef.current = false;
    } else if (onlyCurrencyChanged) {
      currencyChangeFromUrlRef.current = true;
      searchParamsWithoutRefreshRef.current = searchWithoutRefresh;

      const urlCurrency = searchParamsToObject.currency;
      if (urlCurrency && (urlCurrency === 'USD' || urlCurrency === 'ARS')) {
        prevCurrencyRef.current = urlCurrency;
        handleFilters('currency', urlCurrency);
      }
      return;
    } else {
      searchParamsWithoutRefreshRef.current = searchWithoutRefresh;
      if (currentEssential) {
        setLastEssentialParams(currentEssential);
      }
      return;
    }

    const urlCurrency = searchParamsToObject.currency;
    if (urlCurrency && (urlCurrency === 'USD' || urlCurrency === 'ARS')) {
      prevCurrencyRef.current = urlCurrency;
    } else {
      try {
        const flightInfo = sessionStorage.getItem('flightInfo');
        if (flightInfo) {
          const parsed = JSON.parse(flightInfo);
          if (parsed.currency && (parsed.currency === 'USD' || parsed.currency === 'ARS')) {
            prevCurrencyRef.current = parsed.currency;
          } else {
            prevCurrencyRef.current = filters.currency;
          }
        } else {
          prevCurrencyRef.current = filters.currency;
        }
      } catch (error) {
        prevCurrencyRef.current = filters.currency;
      }
    }
    prevCabinClassRef.current = filters.cabinClass;
    isInitialCurrencySyncRef.current = false;
  }, [searchWithoutRefresh, searchParamsToObject.currency, filters.currency, getEssentialSearchParams, getLastEssentialParams, setLastEssentialParams]);

  const handleRetry = useCallback(async () => {
    if (!hasValidSearchParams) {
      return;
    }

    setError(false);
    setIsLoading(true);

    const {
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
      isOnlyOneWay: urlIsOnlyOneWayRetry,
    } = searchParamsToObject;

    if (getCacheStatus() === true) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
    }

    const cacheValue = getCacheStatus();

    const isOnlyOneWayRetry = urlIsOnlyOneWayRetry === 'true' || urlIsOnlyOneWayRetry === true;
    
    const cleanedReturnDateRetry = isOnlyOneWayRetry ? null : returnDate;
    
    setFlightInformation((prevFlightInfo) => {
      if (isOnlyOneWayRetry !== prevFlightInfo?.isOnlyOneWay) {
        setOneWayFlight(isOnlyOneWayRetry);
      }

      return {
        ...prevFlightInfo,
        destinationId,
        destinationName,
        originId,
        originName,
        departureDate,
        returnDate: cleanedReturnDateRetry,
        passengers,
        isOnlyOneWay: isOnlyOneWayRetry,
      };
    });

    const data = {
      originId,
      destinationID: destinationId,
      origin: originName,
      destination: destinationName,
      passengers,
      departureDate,
      returnDate: cleanedReturnDateRetry,
      direct: false,
      baggage: false,
      cabin: filters.cabinClass,
      currency: filters.currency,
      cache: cacheValue,
      delegation,
    };

    try {
      const dataResponse = await PackagesApi.fetchLlegoData(data);
      setData(dataResponse?.data || []);
      handleCacheExpiration(
        dataResponse?.cacheExpiresAt ||
        new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME)
      );
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
      setError(false);
    } catch (error) {
      let errorMessage = 'Error al cargar los vuelos';

      if (error?.response?.status === 500) {
        errorMessage = 'Error del servidor. Por favor, intenta nuevamente en unos momentos.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'No se encontraron vuelos con los criterios especificados.';
      } else if (error?.code === 'ERR_NETWORK') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
      setError(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    hasValidSearchParams,
    searchParamsToObject,
    filters.cabinClass,
    filters.currency,
    setFlightInformation,
    handleCacheExpiration,
    setOneWayFlight,
    delegation,
    setData,
    setError,
    showError,
    setIsLoading
  ]);

  useEffect(() => {
    const paramsWithoutCurrency = new URLSearchParams(searchWithoutRefresh);
    paramsWithoutCurrency.delete('currency');
    const searchWithoutCurrency = paramsWithoutCurrency.toString();

    const prevParamsWithoutCurrency = searchParamsWithoutRefreshRef.current
      ? new URLSearchParams(searchParamsWithoutRefreshRef.current)
      : new URLSearchParams();
    prevParamsWithoutCurrency.delete('currency');
    const prevSearchWithoutCurrency = prevParamsWithoutCurrency.toString();

    const onlyCurrencyChanged = searchParamsWithoutRefreshRef.current !== null &&
      searchWithoutCurrency === prevSearchWithoutCurrency &&
      currencyChangeFromUrlRef.current;

    if (onlyCurrencyChanged && hasCalledApiRef.current) {
      return;
    }

    if (
      hasValidSearchParams &&
      !isLoading &&
      !hasCalledApiRef.current &&
      searchWithoutRefresh &&
      searchParamsWithoutRefreshRef.current === searchWithoutRefresh
    ) {
      hasCalledApiRef.current = true;

      const urlCurrency = searchParamsToObject.currency;
      if (urlCurrency && (urlCurrency === 'USD' || urlCurrency === 'ARS')) {
        prevCurrencyRef.current = urlCurrency;
      } else {
        prevCurrencyRef.current = filters.currency;
      }
      prevCabinClassRef.current = filters.cabinClass;

      isInitialCurrencySyncRef.current = false;
      currencyChangeFromUrlRef.current = false;

      if (onSearchCount === 0) {
        registerOnSearch();
      }
      
      const currentEssential = getEssentialSearchParams(searchWithoutRefresh);
      setLastEssentialParams(currentEssential);
      
      handleLlegoDataRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidSearchParams, searchWithoutRefresh, isLoading, onSearchCount, registerOnSearch, searchParamsToObject, getEssentialSearchParams]);

  useEffect(() => {
    const currencyChanged = prevCurrencyRef.current !== filters.currency;
    const cabinClassChanged = prevCabinClassRef.current !== filters.cabinClass;

    if (
      hasValidSearchParams &&
      hasCalledApiRef.current &&
      (currencyChanged || cabinClassChanged)
    ) {
      if (currencyChangeFromUrlRef.current && currencyChanged && !cabinClassChanged) {
        currencyChangeFromUrlRef.current = false;
        prevCurrencyRef.current = filters.currency;
        prevCabinClassRef.current = filters.cabinClass;
        handleLlegoData();
        return;
      }

      if (isInitialCurrencySyncRef.current && currencyChanged && !cabinClassChanged) {
        isInitialCurrencySyncRef.current = false;
        prevCurrencyRef.current = filters.currency;
        return;
      }

      isInitialCurrencySyncRef.current = false;
      currencyChangeFromUrlRef.current = false;
      prevCurrencyRef.current = filters.currency;
      prevCabinClassRef.current = filters.cabinClass;
      handleLlegoData();
    }
  }, [filters.currency, filters.cabinClass, hasValidSearchParams, handleLlegoData, searchParamsToObject.currency]);

  if (onSearchCount === 0 && !hasValidSearchParams) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>Busca vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Completa el formulario de búsqueda para encontrar los mejores vuelos
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className='container flight__list-view__area loading-flights'
        style={{ textAlign: 'center', padding: '4rem 2rem' }}
      >
        <LoadSpinner />
        <h2 style={{ marginTop: '2rem' }}>Buscando los mejores vuelos para ti</h2>
        <p style={{ marginTop: '1rem', color: '#666' }}>Esto puede tomar unos segundos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--error'>
        <h2>¡Ups! Algo salió mal</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          No pudimos cargar los vuelos. Por favor, intenta nuevamente en unos momentos o ajuste los
          filtros de búsqueda.
        </p>
        <button
          onClick={handleRetry}
          style={{
            backgroundColor: background,
            color: text,
            border: `2px solid ${background}`,
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            marginTop: '20px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = background;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = background;
            e.target.style.color = text;
          }}
        >
          🔄 Reintentar búsqueda
        </button>
        <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
      </div>
    );
  }

  if (!hasValidSearchParams) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>No encontramos vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Por favor, completa todos los campos de búsqueda para ver los vuelos disponibles.
        </p>
      </div>
    );
  }

  if (!filteredData || filteredData.length === 0) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>No encontramos vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Prueba ajustando los filtros de búsqueda o seleccionando fechas diferentes.
        </p>
      </div>
    );
  }

  return (
    <div className='flight__list-view__area'>
      <Flex className='flight__list__view__wrapper--flight'>
        <Flex className='flight__list__view__wrapper--flight__results' vertical>
          {filteredData.map((flightData, index) => {
            if (!flightData || !flightData.flight || flightData.flight.length === 0) {
              return null;
            }
            return <FlightCard {...flightData} key={`${flightData.currency}-${index}`} />;
          })}
        </Flex>
      </Flex>
      <RefreshModal
        disableBackdropClick
        disableEscapeKeyDown
        onClose={() => setShowRefreshModal(false)}
        onConfirm={() => handleRefreshConfirm(handleRefresh)}
        open={showRefreshModal}
      />
      <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
    </div>
  );
};

export default memo(FlightsListView);
